import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { UserPlus, LogIn } from 'lucide-react';
import { authPost } from './portalApi';
import { inputStyle, labelStyle, helpStyle } from '../quote/quoteStyles';

const Field = ({ id, label, type = 'text', value, onChange, autoComplete, minLength }) => (
  <div>
    <label htmlFor={id} style={labelStyle}>{label}</label>
    <input id={id} data-testid={id} type={type} required value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete} minLength={minLength} style={inputStyle} />
  </div>
);

export const PortalAuth = ({ onAuthed }) => {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setInfo('');
    try {
      if (mode === 'forgot') {
        const data = await authPost('forgot-password', { email: form.email });
        setInfo(data.message);
      } else {
        const payload = mode === 'register' ? form : { email: form.email, password: form.password };
        const data = await authPost(mode, payload);
        onAuthed(data.user);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const tab = (key, label, Icon) => (
    <button type="button" role="tab" aria-selected={mode === key} data-testid={`portal-tab-${key}`} onClick={() => { setMode(key); setError(''); setInfo(''); }} style={{
      flex: 1, padding: '0.85rem', border: 'none', cursor: 'pointer', fontWeight: '700', fontSize: '0.95rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
      backgroundColor: mode === key ? 'var(--brand-primary)' : 'var(--bg-page)', color: mode === key ? '#fff' : 'var(--text-primary)'
    }}><Icon size={16} /> {label}</button>
  );

  return (
    <div style={{ maxWidth: '460px', margin: '0 auto', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '12px', overflow: 'hidden' }}>
      <div role="tablist" style={{ display: 'flex' }}>{tab('login', 'Sign in', LogIn)}{tab('register', 'Create account', UserPlus)}</div>
      <form onSubmit={submit} data-testid="portal-auth-form" style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
        {mode === 'forgot' && <p className="body-small" style={{ color: 'var(--text-secondary)', margin: 0 }}>Enter your email and we'll send a reset link.</p>}
        {mode === 'register' && <Field id="portal-name" label="Full name" value={form.name} onChange={set('name')} autoComplete="name" />}
        <Field id="portal-email" label="Email" type="email" value={form.email} onChange={set('email')} autoComplete="email" />
        {mode !== 'forgot' && <Field id="portal-password" label="Password" type="password" value={form.password} onChange={set('password')} autoComplete={mode === 'register' ? 'new-password' : 'current-password'} minLength={8} />}
        {mode === 'register' && <p style={{ ...helpStyle, marginTop: '-0.5rem' }}>At least 8 characters.</p>}
        {error && <p data-testid="portal-auth-error" role="alert" style={{ color: '#c53030', fontSize: '0.9rem', margin: 0 }}>{error}</p>}
        {info && <p data-testid="portal-auth-info" style={{ color: '#2f855a', fontSize: '0.9rem', margin: 0 }}>{info}</p>}
        <button type="submit" className="btn-primary" data-testid="portal-auth-submit" disabled={loading} style={{ width: '100%' }}>
          {loading ? 'Please wait...' : mode === 'login' ? 'Sign in' : mode === 'register' ? 'Create my account' : 'Send reset link'}
        </button>
        {mode === 'login' && <button type="button" data-testid="portal-forgot-link" onClick={() => setMode('forgot')} style={{ background: 'none', border: 'none', color: 'var(--brand-primary)', cursor: 'pointer', fontSize: '0.9rem' }}>Forgot your password?</button>}
        {mode === 'forgot' && <button type="button" onClick={() => setMode('login')} style={{ background: 'none', border: 'none', color: 'var(--brand-primary)', cursor: 'pointer', fontSize: '0.9rem' }}>Back to sign in</button>}
        <p className="body-small" style={{ color: 'var(--text-secondary)', margin: 0, textAlign: 'center' }}>
          By continuing you agree to our <Link to="/eula" style={{ color: 'var(--brand-primary)' }}>Terms</Link> and <Link to="/privacy" style={{ color: 'var(--brand-primary)' }}>Privacy Policy</Link>.
        </p>
      </form>
    </div>
  );
};
